import { useState, useEffect } from 'react';
import api from '../../services/api';

export default function AssignStudentsModal({ isOpen, onClose, batchId, onSuccess }) {
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableStudents();
    }
  }, [isOpen, search]);

  const fetchAvailableStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      // Every student not yet in a batch. No coaching-flag filter: that flag is
      // the student's own onboarding answer and was hiding students a center
      // actually enrolled; no 50 cap: the pool must be complete to pick from.
      const result = await api.getUsers({
        search,
        limit: 500,
        hasNoTeacher: true,
        sortBy: 'name',
        sortDir: 'asc',
      });
      setStudents(result.data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStudent = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedStudents.length === 0) {
      setError('Please select at least one student');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.addStudentsToBatch(batchId, selectedStudents);
      onSuccess();
      onClose();
      setSelectedStudents([]);
      setSearch('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-teacher-border">
          <h2 className="text-xl font-semibold text-teacher-text-primary">Assign Students to Teacher</h2>
          <p className="text-sm text-teacher-text-secondary mt-1">
            Students not yet in any batch
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="p-6 space-y-4 flex-1 overflow-auto">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div>
              <input
                type="text"
                placeholder="Search by name or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-2 border border-teacher-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {loading ? (
              <div className="text-center py-8 text-teacher-text-secondary">
                Loading students...
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-8 text-teacher-text-secondary">
                No students found — everyone is already in a batch, or nothing matches the search.
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-teacher-text-secondary mb-2">
                  {selectedStudents.length} student(s) selected
                </div>
                {students.map((student) => (
                  <label
                    key={student.id}
                    className="flex items-center gap-3 p-3 border border-teacher-border rounded-lg hover:bg-teacher-bg cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => handleToggleStudent(student.id)}
                      className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-teacher-text-primary">
                        {student.firstName} {student.lastName}
                      </div>
                      <div className="text-xs text-teacher-text-secondary">
                        {student.phoneNumber}{student.examDate ? ` • JEE ${student.examDate}` : ''}{student.isEnrolledInCoaching ? ' • coaching' : ''}
                      </div>
                    </div>
                    {student.overall_percentile !== undefined && (
                      <div className="text-xs text-teacher-text-secondary">
                        {Math.round(student.overall_percentile)}% percentile
                      </div>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="p-6 border-t border-teacher-border flex gap-3">
            <button
              type="button"
              onClick={() => {
                onClose();
                setSelectedStudents([]);
                setSearch('');
              }}
              className="flex-1 px-4 py-2 border border-teacher-border-strong rounded-lg text-teacher-text-primary hover:bg-teacher-bg transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              disabled={submitting || selectedStudents.length === 0}
            >
              {submitting ? 'Assigning...' : `Assign ${selectedStudents.length} Student(s)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
